import type { AssetContainer } from '@babylonjs/core/assetContainer';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { Scene } from '@babylonjs/core/scene';
import type { Skeleton } from '@babylonjs/core/Bones/skeleton';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector';
// Side effect: adds `thinInstanceSetBuffer` and friends to Mesh. Without it `placeMany` throws.
import '@babylonjs/core/Meshes/thinInstanceMesh';

import { renderFeatures } from '../compat';
import type { StationKind } from '../../net/protocol';
import { loadGlbContainer, PROP_SELF_LIGHT } from './gltf';

/**
 * Generated props: painted static meshes standing where procedural geometry stood.
 *
 * The contract is `doc/grind/architecture/GENERATED-PROPS.md`. In one line: **a prop is cosmetic,
 * always** — it never changes a tile, a collider, a click target or anything the server knows, and
 * when it is missing the geometry it replaced is built instead.
 */

/**
 * How a prop is fitted: which of its extents is scaled to the size the caller asks for.
 *
 * `width` is the larger of X and Z — a stall fitted to the stall it replaces. `height` is Y — a
 * figure fitted to the figure, a post to the post.
 */
export type PropFit = 'width' | 'height';

export interface PropSpec {
  readonly url: string;
  /** The triangle budget `scripts/build-prop.sh` was run with; `props.test.ts` holds the file to it. */
  readonly tris: number;
  readonly fit: PropFit;
  /**
   * The size its `fit` extent is scaled to, in world units — a character is 1.64 tall. For a
   * station, **chosen by looking**: the first version fitted every station to the width of the
   * stall it replaced, which is right for a counter and made the arcane vault's lectern a
   * two-storey monolith and the stash a shed. A caller may pass its own size instead (a vendor
   * fitted to the box shopkeeper, a lamp to the post) — see `place`.
   */
  readonly size?: number;
  /**
   * How much it lights itself, as a fraction of its own texture, where `PROP_SELF_LIGHT` is not
   * enough. For a prop painted near-black that stands in the unlit void past the walls: the dead
   * forest's tree averages 13% brightness, and at the default it was a hole in a black sky.
   */
  readonly selfLight?: number;
  /**
   * The most it may stand, in world units, whatever `size` makes of it. For terrain: an obstacle
   * must stay under the walls' 2.6 or it reads as part of them and hides the monsters behind it
   * (`OBSTACLE_HEIGHT` in `zone/render.ts`) — so a broken column fitted to its tile's width would
   * be a tower, and is shrunk to this instead.
   */
  readonly maxHeight?: number;
  /**
   * Radians added to the placement's yaw so the model's own front faces the placement's front
   * (+Z local). Chosen per prop, by looking: every file here was checked from ±Z and ±X, and every
   * one faces +Z already, so they are all zero — a regenerated file may not be.
   */
  readonly yaw: number;
  /**
   * Stand it on a low rock mound the size of the obstacle it fills. For a **thin** prop on an
   * `OBSTACLE` tile — a totem, a lantern post, a statue — whose own footprint is a fraction of the
   * 8×8 the server holds solid: without the mound a body stops at an invisible edge metres from
   * anything drawn, the mismatch the owner reported of walls. Chosen per prop, by looking.
   */
  readonly mound?: boolean;
}

const url = (file: string): string => new URL(`../../assets/props/${file}.glb`, import.meta.url).href;

export const PROPS = {
  station_forge: { url: url('station_forge'), tris: 4000, fit: 'width', size: 3.0, yaw: 0 },
  station_tannery: { url: url('station_tannery'), tris: 4000, fit: 'height', size: 2.6, yaw: 0 },
  station_atelier: { url: url('station_atelier'), tris: 4000, fit: 'width', size: 3.0, yaw: 0 },
  station_woodworks: { url: url('station_woodworks'), tris: 4000, fit: 'width', size: 3.2, yaw: 0 },
  station_arcane_vault: { url: url('station_arcane_vault'), tris: 4000, fit: 'height', size: 2.0, yaw: 0 },
  station_stash: { url: url('station_stash'), tris: 4000, fit: 'width', size: 1.8, yaw: 0 },
  town_portal: { url: url('town_portal'), tris: 4000, fit: 'height', yaw: 0 },
  town_lamp_post: { url: url('town_lamp_post'), tris: 1500, fit: 'height', yaw: 0 },
  marker_exit_stairway: { url: url('marker_exit_stairway'), tris: 3000, fit: 'width', yaw: 0 },
  marker_grind_entrance_obelisk: { url: url('marker_grind_entrance_obelisk'), tris: 1500, fit: 'height', yaw: 0 },
  // The crypts' terrain — *fallen columns and collapsed masonry* (`TerrainProfile`, biome.go) — drawn
  // on OBSTACLE tiles, which the server already holds solid. Inside the block they replace (4.8
  // across on an 8-unit tile) and under the walls. Chosen by looking.
  crypt_sarcophagus: { url: url('crypt_sarcophagus'), tris: 3000, fit: 'width', size: 4.2, maxHeight: 2.3, yaw: 0 },
  crypt_broken_pillar: { url: url('crypt_broken_pillar'), tris: 3000, fit: 'width', size: 3.6, maxHeight: 2.5, yaw: 0 },
  crypt_bone_pile: { url: url('crypt_bone_pile'), tris: 3000, fit: 'width', size: 4.2, maxHeight: 2.3, yaw: 0 },
  crypt_candle_altar: { url: url('crypt_candle_altar'), tris: 3000, fit: 'width', size: 3.4, maxHeight: 2.4, yaw: 0 },
  // Wall-mounted, on the far walls of a crypt room. **Its front is +X in the file** — the wall plate
  // is at −X and the torch stands off it — so a quarter turn brings it to the +Z convention.
  crypt_wall_torch: { url: url('crypt_wall_torch'), tris: 1500, fit: 'height', size: 1.3, yaw: -Math.PI / 2 },
  // Gear, from the items' inventory icons (`gear.ts`). Not placed on the floor: `fit` and `size`
  // are unused, because each is fitted to the procedural piece it replaces. Helmets face +Z.
  gear_head_cloth: { url: url('gear_head_cloth'), tris: 2000, fit: 'width', yaw: 0 },
  gear_head_leather: { url: url('gear_head_leather'), tris: 2000, fit: 'width', yaw: 0 },
  gear_head_plate: { url: url('gear_head_plate'), tris: 2000, fit: 'width', yaw: 0 },
  gear_weapon_sword_1h: { url: url('gear_weapon_sword_1h'), tris: 1500, fit: 'height', yaw: 0 },
  gear_weapon_sword_2h: { url: url('gear_weapon_sword_2h'), tris: 1500, fit: 'height', yaw: 0 },
  gear_weapon_axe_2h: { url: url('gear_weapon_axe_2h'), tris: 1500, fit: 'height', yaw: 0 },
  gear_weapon_mace_1h: { url: url('gear_weapon_mace_1h'), tris: 1500, fit: 'height', yaw: 0 },
  gear_weapon_mace_2h: { url: url('gear_weapon_mace_2h'), tris: 1500, fit: 'height', yaw: 0 },
  gear_weapon_dagger: { url: url('gear_weapon_dagger'), tris: 1500, fit: 'height', yaw: 0 },
  gear_offhand_buckler: { url: url('gear_offhand_buckler'), tris: 1500, fit: 'height', yaw: 0 },
  gear_offhand_shield: { url: url('gear_offhand_shield'), tris: 1500, fit: 'height', yaw: 0 },
  gear_offhand_tome: { url: url('gear_offhand_tome'), tris: 1500, fit: 'height', yaw: 0 },
  gear_offhand_orb: { url: url('gear_offhand_orb'), tris: 1500, fit: 'height', yaw: 0 },
  // The other six biomes' terrain (2026-09-29), from the owner's images through Hunyuan3D-2 — on
  // OBSTACLE tiles, under the walls' 2.6 like the crypt set. Sizes chosen by looking.
  abyss_floating_rune_monolith: { url: url('abyss_floating_rune_monolith'), tris: 3000, fit: 'height', size: 2.4, maxHeight: 2.5, yaw: 0, mound: true },
  abyss_twisted_tendril: { url: url('abyss_twisted_tendril'), tris: 3000, fit: 'height', size: 2.4, maxHeight: 2.5, yaw: 0, mound: true },
  caldera_charred_brazier: { url: url('caldera_charred_brazier'), tris: 3000, fit: 'width', size: 2.4, maxHeight: 1.8, yaw: 0, mound: true },
  thicket_hunters_totem: { url: url('thicket_hunters_totem'), tris: 3000, fit: 'height', size: 2.4, maxHeight: 2.5, yaw: 0, mound: true },
  thicket_dead_tree: { url: url('thicket_dead_tree'), tris: 3000, fit: 'width', size: 4.4, maxHeight: 2.5, yaw: 0 },
  thicket_thorn_bush: { url: url('thicket_thorn_bush'), tris: 3000, fit: 'width', size: 4.2, maxHeight: 2.2, yaw: 0 },
  wastes_bleached_ribcage: { url: url('wastes_bleached_ribcage'), tris: 3000, fit: 'width', size: 4.4, maxHeight: 2.4, yaw: 0 },
  wastes_cracked_statue: { url: url('wastes_cracked_statue'), tris: 3000, fit: 'height', size: 2.5, maxHeight: 2.5, yaw: 0, mound: true },
  marsh_hanging_lantern: { url: url('marsh_hanging_lantern'), tris: 3000, fit: 'height', size: 2.5, maxHeight: 2.5, yaw: 0, mound: true },
  // Decoration drawn many times as thin instances (`placeMany`), so a low budget: a forest edge is
  // hundreds of these in one draw call.
  deco_tree: { url: url('deco_tree'), tris: 400, fit: 'height', size: 4.5, yaw: 0, selfLight: 4 },
  // Rigged (`build-body.sh`), and drawn in their bind pose: arms baked down, standing still. A
  // shopkeeper is scenery, not an entity, so nothing ever poses them.
  vendor_blacksmith: { url: url('vendor_blacksmith'), tris: 6000, fit: 'height', yaw: 0 },
  vendor_shopkeeper: { url: url('vendor_shopkeeper'), tris: 6000, fit: 'height', yaw: 0 },
} as const satisfies Record<string, PropSpec>;

export type PropId = keyof typeof PROPS;

/**
 * Each biome's obstacle dressing, one picked per `OBSTACLE` tile by hash. A biome not listed — the
 * Sunless Caverns, which has no generated terrain yet — keeps its procedural block. Chosen: which
 * prop belongs to which biome follows the images' own names.
 */
export const BIOME_OBSTACLES: Readonly<Record<string, readonly PropId[]>> = {
  BIOME_CRYPTS: ['crypt_sarcophagus', 'crypt_broken_pillar', 'crypt_bone_pile', 'crypt_candle_altar'],
  BIOME_ABYSS: ['abyss_floating_rune_monolith', 'abyss_twisted_tendril'],
  BIOME_CALDERA: ['caldera_charred_brazier'],
  BIOME_THICKET: ['thicket_dead_tree', 'thicket_thorn_bush', 'thicket_hunters_totem'],
  BIOME_WASTES: ['wastes_bleached_ribcage', 'wastes_cracked_statue'],
  BIOME_MARSH: ['marsh_hanging_lantern'],
};

/** The crypts' set, as it was named before the other biomes had theirs. */
export const CRYPT_OBSTACLES: readonly PropId[] = BIOME_OBSTACLES.BIOME_CRYPTS!;

/** The prop that replaces a station's stall — the `StationKind` spelled in lower case. */
export function stationProp(kind: StationKind): PropId {
  return `station_${kind.toLowerCase()}` as PropId;
}

/**
 * Who stands at which counter. **Chosen, not specified** (spec §8, question 1): the two vendor
 * images the owner made are a blacksmith and a shopkeeper, and the blacksmith goes where the work
 * is hammering and sawing.
 */
export const VENDOR_FOR: Readonly<Record<StationKind, PropId>> = {
  FORGE: 'vendor_blacksmith',
  WOODWORKS: 'vendor_blacksmith',
  TANNERY: 'vendor_shopkeeper',
  ATELIER: 'vendor_shopkeeper',
  ARCANE_VAULT: 'vendor_shopkeeper',
  STASH: 'vendor_shopkeeper',
};

/** Where a prop goes: a point on the floor and the way its front faces. */
export interface PropPlacement {
  x: number;
  z: number;
  yaw: number;
  y?: number;
}

/** One placed prop. Dispose it through `PropLibrary.release`, never through a parent. */
export interface PlacedProp {
  readonly root: TransformNode;
  readonly meshes: readonly Mesh[];
}

/** A container's extent in its own units, measured once. */
interface Extent {
  min: Vector3;
  max: Vector3;
}

/**
 * Loads the props once and places copies of them.
 *
 * ── Why the placed meshes leave the glTF hierarchy ─────────────────────────────────────
 *
 * The loader hangs a file under a `__root__` that flips handedness. The skinned bodies taught the
 * cost of keeping it: bone nodes under it read mirrored in X. So each placed mesh is re-parented
 * straight onto the prop's own node, exactly as `attachSkinnedBody` does — the rendered geometry is
 * the file's own, and a vendor's skeleton, left behind under `__root__`, is only ever read at its
 * bind pose.
 *
 * ── Why a placed prop must not be disposed through the zone root ───────────────────────
 *
 * `ZoneRenderer.clear` disposes its root **with materials and textures**, which is right for
 * everything it built — and wrong here, because every copy of a prop shares its container's one
 * material. Disposed through the zone, the second visit to town would draw every prop with a
 * dead material: invisible, with a shadow on the floor, which is the fault `gltf.ts` exists to
 * stop. So props hang off their own node and `release` disposes geometry only.
 */
export class PropLibrary {
  private readonly containers = new Map<PropId, AssetContainer>();
  private readonly extents = new Map<PropId, Extent>();
  private readonly skeletons = new Map<TransformNode, Skeleton[]>();

  constructor(private readonly scene: Scene) {}

  /**
   * Loads every prop, all at once, before any zone needs one.
   *
   * **Fire and forget, as the bodies are.** A zone built before this resolves draws its
   * procedural geometry, and `ZoneRenderer.refresh` rebuilds it once the props land — so a slow
   * fetch costs a frame of boxes, never a loading screen. `?props=0` and safe mode return at once
   * and nothing is fetched (2.5 MB).
   */
  async preload(): Promise<void> {
    if (!renderFeatures.props) return;
    await Promise.all(
      (Object.keys(PROPS) as PropId[]).map(async (id) => {
        const spec: PropSpec = PROPS[id];
        const container = await loadGlbContainer(this.scene, spec.url, spec.selfLight ?? PROP_SELF_LIGHT);
        if (container) this.adopt(id, container);
      }),
    );
  }

  /** Registers a loaded container. Public for the tests, which load from bytes. */
  adopt(id: PropId, container: AssetContainer): void {
    const meshes = container.meshes.filter((m) => m.getTotalVertices() > 0);
    if (meshes.length === 0) {
      console.error(`grind: prop ${id} has no mesh — drawing the procedural geometry instead`);
      return;
    }
    const min = new Vector3(Infinity, Infinity, Infinity);
    const max = new Vector3(-Infinity, -Infinity, -Infinity);
    for (const mesh of meshes) {
      // The mesh's own box, in the file's units — the frame the placed copy is drawn in once it
      // leaves `__root__`. A skinned vendor's is its bind pose, which is the pose it stands in.
      const box = mesh.getBoundingInfo().boundingBox;
      min.minimizeInPlace(box.minimum);
      max.maximizeInPlace(box.maximum);
    }
    this.containers.set(id, container);
    this.extents.set(id, { min, max });
  }

  has(id: PropId): boolean {
    return this.containers.has(id);
  }

  /**
   * Places one copy, standing on the floor at `at`, facing `at.yaw`, scaled so its `fit` extent
   * is `size` world units — or the table's own `size` when the caller passes none. Returns `null`
   * when the prop is not loaded (or has no size at all): the caller builds its procedural geometry.
   */
  place(id: PropId, parent: TransformNode, at: PropPlacement, size: number | undefined, name: string): PlacedProp | null {
    const container = this.containers.get(id);
    const extent = this.extents.get(id);
    if (!container || !extent) return null;
    const spec: PropSpec = PROPS[id];
    const target = size ?? spec.size;
    if (target === undefined) return null;

    const width = Math.max(extent.max.x - extent.min.x, extent.max.z - extent.min.z);
    const height = extent.max.y - extent.min.y;
    let scale = target / (spec.fit === 'width' ? width : height);
    if (spec.maxHeight !== undefined) scale = Math.min(scale, spec.maxHeight / height);

    // holder: where, which way, how big. inner: the file's own offset, so it stands on Y 0 and is
    // centred over the point — in file units, since the holder's scale applies to it.
    const holder = new TransformNode(name, this.scene);
    holder.parent = parent;
    holder.position.set(at.x, at.y ?? 0, at.z);
    holder.rotation.y = at.yaw + spec.yaw;
    holder.scaling.setAll(scale);
    const inner = new TransformNode(`${name}-fit`, this.scene);
    inner.parent = holder;
    inner.position.set(
      -(extent.min.x + extent.max.x) / 2,
      -extent.min.y,
      -(extent.min.z + extent.max.z) / 2,
    );

    const entries = container.instantiateModelsToScene((source) => `${name}-${source}`, false);
    const meshes = entries.rootNodes
      .flatMap((node) => [node, ...node.getChildMeshes(false)])
      .filter((node): node is Mesh => (node as Mesh).getTotalVertices?.() > 0);
    for (const mesh of meshes) {
      mesh.parent = inner;
      mesh.position.setAll(0);
      mesh.rotation.setAll(0);
      mesh.rotationQuaternion = null;
      mesh.scaling.setAll(1);
      mesh.isPickable = false;
      // Receives and does not cast, the rule the town's walls follow: a low sun through the
      // stalls would lay long slabs of shadow across the street.
      mesh.receiveShadows = true;
    }
    // What stays behind under `__root__` is a vendor's bone nodes and the empty root itself.
    for (const node of entries.rootNodes) if (!meshes.includes(node as Mesh)) node.parent = inner;
    this.skeletons.set(holder, entries.skeletons);
    return { root: holder, meshes };
  }

  /**
   * One copy's meshes, **in the file's own frame and parented to nothing** — for a caller that
   * fits the model itself (`gear.ts`, which fits a weapon to the procedural one it replaces). The
   * glTF's empty `__root__` is disposed; what it held is returned. Dispose the meshes geometry
   * only, never with their materials, which every copy shares. `null` when the prop is not loaded.
   */
  instantiate(id: PropId, name: string): Mesh[] | null {
    const container = this.containers.get(id);
    if (!container) return null;
    const entries = container.instantiateModelsToScene((source) => `${name}-${source}`, false);
    const meshes = entries.rootNodes
      .flatMap((node) => [node, ...node.getChildMeshes(false)])
      .filter((node): node is Mesh => (node as Mesh).getTotalVertices?.() > 0);
    for (const mesh of meshes) mesh.parent = null;
    for (const skeleton of entries.skeletons) skeleton.dispose();
    return meshes;
  }

  /**
   * Many copies in **one draw call**: a single instantiated copy whose meshes carry a thin instance
   * per placement. For decoration counted in hundreds — the trees round a forest's rooms — where a
   * `place` per copy would be a mesh and a draw call each. Each placement is fitted exactly as
   * `place` fits one (`fit`, the file's own offset, `yaw`), at its own `size`. Not pickable, casts
   * no shadow. `null` when the prop is not loaded or there is nothing to place.
   */
  placeMany(id: PropId, parent: TransformNode, at: readonly (PropPlacement & { size: number })[], name: string): PlacedProp | null {
    const container = this.containers.get(id);
    const extent = this.extents.get(id);
    if (!container || !extent || at.length === 0) return null;
    const spec: PropSpec = PROPS[id];

    const holder = new TransformNode(name, this.scene);
    holder.parent = parent;
    const entries = container.instantiateModelsToScene((source) => `${name}-${source}`, false);
    const meshes = entries.rootNodes
      .flatMap((node) => [node, ...node.getChildMeshes(false)])
      .filter((node): node is Mesh => (node as Mesh).getTotalVertices?.() > 0);

    const width = Math.max(extent.max.x - extent.min.x, extent.max.z - extent.min.z);
    const height = extent.max.y - extent.min.y;
    const offset = Matrix.Translation(-(extent.min.x + extent.max.x) / 2, -extent.min.y, -(extent.min.z + extent.max.z) / 2);
    const matrices = new Float32Array(at.length * 16);
    at.forEach((p, i) => {
      let scale = p.size / (spec.fit === 'width' ? width : height);
      if (spec.maxHeight !== undefined) scale = Math.min(scale, spec.maxHeight / height);
      offset
        .multiply(Matrix.Scaling(scale, scale, scale))
        .multiply(Matrix.RotationY(p.yaw + spec.yaw))
        .multiply(Matrix.Translation(p.x, p.y ?? 0, p.z))
        .copyToArray(matrices, i * 16);
    });
    for (const mesh of meshes) {
      mesh.parent = holder;
      mesh.position.setAll(0);
      mesh.rotation.setAll(0);
      mesh.rotationQuaternion = null;
      mesh.scaling.setAll(1);
      mesh.isPickable = false;
      mesh.thinInstanceSetBuffer('matrix', matrices, 16);
      // Thin instances are culled by the base mesh's box unless told otherwise; the copies are
      // spread over the whole zone.
      mesh.thinInstanceRefreshBoundingInfo(false);
    }
    // Only now: disposing the file's empty `__root__` disposes its children, and until the loop
    // above the meshes were its children. Done first, it took every tree with it.
    for (const node of entries.rootNodes) if (!meshes.includes(node as Mesh)) node.dispose(false, false);
    return { root: holder, meshes };
  }

  /** Disposes a placed copy's geometry and skeleton — never the shared material. */
  release(prop: PlacedProp): void {
    for (const skeleton of this.skeletons.get(prop.root) ?? []) skeleton.dispose();
    this.skeletons.delete(prop.root);
    prop.root.dispose(false, false);
  }

  dispose(): void {
    for (const container of this.containers.values()) container.dispose();
    this.containers.clear();
    this.extents.clear();
  }
}
